/**
 * Protocollo della chat.
 *
 * L'unità di risposta di Otto non è il testo: è il blocco. L'agente emette
 * blocchi tipizzati, il frontend li rende con la libreria componenti. Il testo
 * è solo il collante fra un blocco e l'altro.
 *
 * I blocchi che si riferiscono a un'azione portano solo l'`actionId`: lo stato
 * dell'azione vive in un unico posto (la mappa azioni del client, alimentata
 * dagli eventi `action_update`), così una card non può mai mostrare uno stato
 * diverso dallo storico.
 */

import { z } from "zod";
import { DatiPropostaDocumentoSchema } from "./documenti.js";
import {
  ActionPreviewSchema,
  SyncReceiptSchema,
  type ActionView,
} from "./actions.js";

// ── Blocchi ──────────────────────────────────────────────────────────────────

const BlockBase = { id: z.string() };

/**
 * Quanto può essere lungo un messaggio dell'host. Erano 4.000 caratteri, circa
 * una pagina: troppo poco per incollare una mail del condominio o un
 * regolamento. 20.000 sono circa cinque pagine, e stanno larghi nel tetto di
 * 1 MB del corpo della richiesta (8/10/2026).
 */
export const CARATTERI_MAX_MESSAGGIO = 20_000;

export const ProposalCardBlockSchema = z.object({
  ...BlockBase,
  type: z.literal("proposal_card"),
  actionId: z.string(),
  /** Copia dell'anteprima al momento della proposta: la card resta leggibile
   *  anche se l'azione viene poi annullata o rieseguita. */
  preview: ActionPreviewSchema,
});

export const DisambiguationOptionSchema = z.object({
  id: z.string(),
  label: z.string(),
  sublabel: z.string().nullable(),
  photoUrl: z.string().nullable(),
  /** Testo inviato come messaggio dell'host quando l'opzione viene toccata:
   *  la trascrizione resta leggibile come una conversazione vera. */
  value: z.string(),
});
export type DisambiguationOption = z.infer<typeof DisambiguationOptionSchema>;

export const DisambiguationBlockSchema = z.object({
  ...BlockBase,
  type: z.literal("disambiguation"),
  question: z.string(),
  options: z.array(DisambiguationOptionSchema).min(2),
});

export const ReceiptBlockSchema = z.object({
  ...BlockBase,
  type: z.literal("receipt"),
  actionId: z.string(),
  receipt: SyncReceiptSchema.nullable(),
});

export const CalendarPreviewDaySchema = z.object({
  date: z.string(),
  priceCents: z.number().int().nullable(),
  minNights: z.number().int().nullable(),
  booked: z.boolean(),
  available: z.boolean(),
});

export const CalendarPreviewBlockSchema = z.object({
  ...BlockBase,
  type: z.literal("calendar_preview"),
  listingId: z.string(),
  nickname: z.string(),
  from: z.string(),
  to: z.string(),
  days: z.array(CalendarPreviewDaySchema),
});

export const CHIP_TONES = ["neutro", "accento", "attenzione"] as const;

export const ChipSchema = z.object({
  id: z.string(),
  label: z.string(),
  /** Testo che finisce nell'input quando la chip viene toccata. Una chip non
   *  esegue mai: prepara il comando, che passa comunque dalla proposta. */
  prompt: z.string(),
  tone: z.enum(CHIP_TONES).default("neutro"),
  icon: z.string().nullable().default(null),
});
export type Chip = z.infer<typeof ChipSchema>;

/**
 * `memoria` è la proposta di ricordare qualcosa: «questa risposta la uso
 * sempre?», con «Sì, segnatelo» e «No, solo stavolta». È una variante a sé e
 * non un follow-up perché è una domanda, non un suggerimento di continuazione:
 * quando c'è lei, gli altri suggerimenti stanno zitti.
 */
export const ChipsBlockSchema = z.object({
  ...BlockBase,
  type: z.literal("chips"),
  variant: z.enum(["briefing", "follow_up", "memoria"]),
  /** La domanda sopra le chip, quando ce n'è una. I suggerimenti di
   *  continuazione non ne hanno: si spiegano da soli. */
  question: z.string().nullable().default(null),
  chips: z.array(ChipSchema).min(1).max(4),
});

export const NoticeBlockSchema = z.object({
  ...BlockBase,
  type: z.literal("notice"),
  tone: z.enum(["info", "attenzione", "errore", "successo"]),
  title: z.string().nullable(),
  message: z.string(),
});

export const ListingsBlockSchema = z.object({
  ...BlockBase,
  type: z.literal("listings"),
  listings: z.array(
    z.object({
      id: z.string(),
      nickname: z.string(),
      city: z.string().nullable(),
      photoUrl: z.string().nullable(),
      /** Un solo dato per card, come da §1.bis. */
      stat: z.string().nullable(),
    }),
  ),
});

/**
 * Un file allegato dall'host, sul suo messaggio: il nome e il tipo, non il
 * contenuto (quello sta una volta sola in `allegati`).
 */
export const AllegatoBlockSchema = z.object({
  ...BlockBase,
  type: z.literal("allegato"),
  allegatoId: z.string(),
  nome: z.string(),
  tipo: z.string(),
});

/**
 * Quello che Otto ha letto in un documento: costi e rate, da controllare e
 * salvare con un tap. Il tap salva la proposta sul server, cioè esattamente
 * queste righe, non una rilettura del modello.
 */
export const DocumentoCostiBlockSchema = z.object({
  ...BlockBase,
  type: z.literal("documento_costi"),
  propostaId: z.string(),
  immobile: z.string().nullable(),
  dati: DatiPropostaDocumentoSchema,
});

export const ChatBlockSchema = z.discriminatedUnion("type", [
  ProposalCardBlockSchema,
  DisambiguationBlockSchema,
  ReceiptBlockSchema,
  CalendarPreviewBlockSchema,
  ChipsBlockSchema,
  NoticeBlockSchema,
  ListingsBlockSchema,
  AllegatoBlockSchema,
  DocumentoCostiBlockSchema,
]);
export type ChatBlock = z.infer<typeof ChatBlockSchema>;
export type ChatBlockType = ChatBlock["type"];

// ── Passi di lavoro visibili ─────────────────────────────────────────────────

export const AGENT_STEP_STATUSES = ["in_corso", "fatto", "errore"] as const;
export type AgentStepStatus = (typeof AGENT_STEP_STATUSES)[number];

export interface AgentStep {
  id: string;
  /** In italiano, da collaboratore: "Sto leggendo il calendario di settembre…" */
  label: string;
  status: AgentStepStatus;
}

// ── Messaggi persistiti ──────────────────────────────────────────────────────

export type ChatRole = "host" | "otto";

export interface ChatMessageView {
  id: string;
  role: ChatRole;
  text: string;
  blocks: ChatBlock[];
  steps: AgentStep[];
  at: string;
}

export interface ChatThreadView {
  id: string;
  title: string;
  createdAt: string;
  lastMessageAt: string;
  messageCount: number;
}

// ── Eventi SSE (server → client) ─────────────────────────────────────────────

export type ChatEvent =
  | { type: "thread"; threadId: string; title: string }
  | { type: "message_start"; messageId: string }
  | { type: "step"; step: AgentStep }
  | { type: "text_delta"; text: string }
  | { type: "block"; block: ChatBlock }
  /**
   * Aggiornamento di un'azione: la card la ascolta e si aggiorna in-place,
   * senza generare un nuovo messaggio.
   *
   * Viaggia la **vista intera**, non due campi. Prima portava solo `status` e
   * `receipt`, e tutto il resto restava fermo a com'era quando la card era
   * nata: dopo un'esecuzione riuscita il tasto «Annulla» non compariva —
   * `canRollback` era ancora quello della proposta — e ricompariva minuti
   * dopo, quando qualcos'altro rileggeva l'azione. Una mappa che è l'unica
   * fonte di verità va aggiornata tutta, o non è l'unica fonte di verità.
   */
  | {
      type: "action_update";
      actionId: string;
      action: ActionView;
    }
  | { type: "message_done"; messageId: string }
  | { type: "error"; message: string }
  | { type: "ping" };

export function encodeSseEvent(event: ChatEvent): string {
  return `data: ${JSON.stringify(event)}\n\n`;
}

// ── Titolo della conversazione ──────────────────────────────────────────────

/** Quanto può essere lungo il titolo di una conversazione. */
export const TITOLO_MAX = 48;

/**
 * Titola una conversazione dal primo messaggio.
 *
 * Tagliare a carattere fisso produce titoli monchi ("…a settembr"): qui si
 * taglia all'ultimo spazio utile e si mette i puntini, così il titolo resta
 * una frase leggibile.
 */
export function titoloConversazione(testo: string): string {
  const pulito = testo.replace(/\s+/g, " ").trim();
  if (pulito === "") return "Nuova conversazione";
  if (pulito.length <= TITOLO_MAX) return pulito;

  const tagliato = pulito.slice(0, TITOLO_MAX);
  const ultimoSpazio = tagliato.lastIndexOf(" ");
  // Se la prima parola da sola supera il limite non c'è spazio a cui appendersi.
  const base =
    ultimoSpazio > TITOLO_MAX / 2 ? tagliato.slice(0, ultimoSpazio) : tagliato;
  return `${base.replace(/[.,;:!?]+$/, "")}…`;
}
